import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Label, TextArea } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { TextChipInput, type TextChipInputHandle } from "@/components/ui/TextChipInput";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

import { searchFirmwareBetaCustomers, type FirmwareBetaCustomer } from "./firmwareApi";

const MODAL_HELP =
  "Choose customers, optional notes, and device serials for this beta send. That information is stored on the firmware file.";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function toOption(row: FirmwareBetaCustomer): SearchableSelectOption {
  return { value: String(row.fsAccountId), label: row.name };
}

export function FirmwareSendToBetaModal({
  open,
  pending,
  errorMessage,
  initialCustomers,
  initialNotes,
  initialSerials,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  errorMessage: string | null;
  initialCustomers: FirmwareBetaCustomer[];
  initialNotes: string;
  initialSerials: string[];
  onClose: () => void;
  onConfirm: (input: { customers: FirmwareBetaCustomer[]; notes: string; serials: string[] }) => void;
}): JSX.Element | null {
  const serialInputRef = useRef<TextChipInputHandle>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedValues, setSelectedValues] = useState<Set<string>>(() => new Set());
  const [knownById, setKnownById] = useState<Map<string, FirmwareBetaCustomer>>(() => new Map());
  const [notes, setNotes] = useState("");
  const [serials, setSerials] = useState<string[]>([]);
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSearchValue("");
    setNotes(initialNotes);
    setSerials([...initialSerials]);
    const nextKnown = new Map<string, FirmwareBetaCustomer>();
    const nextSelected = new Set<string>();
    for (const customer of initialCustomers) {
      const id = String(customer.fsAccountId);
      nextKnown.set(id, customer);
      nextSelected.add(id);
    }
    setKnownById(nextKnown);
    setSelectedValues(nextSelected);
  }, [open, initialCustomers, initialNotes, initialSerials]);

  const searchQuery = debouncedSearch.length >= 2 ? debouncedSearch : "";
  const searchResultsQuery = useQuery({
    queryKey: ["engineering", "firmware", "beta-customers", searchQuery],
    queryFn: () => searchFirmwareBetaCustomers(searchQuery),
    enabled: open && searchQuery.length >= 2,
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownById((previous) => {
      const next = new Map(previous);
      for (const row of rows) {
        next.set(String(row.fsAccountId), row);
      }
      return next;
    });
  }, [searchResultsQuery.data]);

  const options = useMemo(() => {
    const merged = new Map<string, SearchableSelectOption>();
    for (const [id, row] of knownById) {
      if (selectedValues.has(id)) {
        merged.set(id, toOption(row));
      }
    }
    for (const row of searchResultsQuery.data?.rows ?? []) {
      merged.set(String(row.fsAccountId), toOption(row));
    }
    return [...merged.values()];
  }, [knownById, searchResultsQuery.data, selectedValues]);

  if (!open) {
    return null;
  }

  const handleConfirm = (): void => {
    const flushedSerials = serialInputRef.current?.flushDraft() ?? serials;
    const customers: FirmwareBetaCustomer[] = [];
    for (const id of selectedValues) {
      const row = knownById.get(id);
      if (row == null) {
        continue;
      }
      customers.push(row);
    }
    onConfirm({ customers, notes, serials: flushedSerials });
  };

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="firmware-send-to-beta-title">
      <div className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="firmware-send-to-beta-title" className="text-lg font-semibold text-slate-900">
              Send to beta customer
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>
        <div className="space-y-3 px-4 py-4 sm:px-5">
          <div className="space-y-1.5">
            <Label htmlFor="firmware-beta-customers">Customers</Label>
            <SearchableMultiSelect
              id="firmware-beta-customers"
              variant="chips"
              addLabel="Add customer"
              noneSelectedLabel="Select customers"
              selectedValues={selectedValues}
              onSelectedValuesChange={setSelectedValues}
              options={options}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              placeholder="Search customers"
              searchPlaceholder="Type at least two characters"
              emptyMessage={
                searchValue.trim().length < 2 ? "Type at least two characters to search." : "No customers match."
              }
              panelMinWidth={320}
            />
          </div>
          <div>
            <Label htmlFor="firmware-beta-notes">Notes</Label>
            <TextArea
              id="firmware-beta-notes"
              className="mt-1.5 font-sans"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="What was sent, and why"
            />
          </div>
          <TextChipInput
            ref={serialInputRef}
            id="firmware-beta-serials"
            label="Device serial numbers"
            chips={serials}
            onChipsChange={setSerials}
            placeholder="Type a serial and press Enter"
          />
          {errorMessage ? <p className="text-sm text-rose-700">{errorMessage}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" disabled={pending || selectedValues.size === 0} onClick={handleConfirm}>
            Save
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
